import { styled } from "@mui/material/styles";
import React from "react";
import { useTranslation } from "react-i18next";
import { CloseIcon } from "./icons";
import { focusRing, pressable } from "./tokens";
const chipStyles = ({ tone })=>({
        ...focusRing,
        display: "inline-flex",
        alignItems: "center",
        gap: "var(--j-sp-1)",
        height: "var(--j-tag-h)",
        maxWidth: "100%",
        padding: "0 var(--j-sp-1-5)",
        borderRadius: "var(--j-r-sm)",
        background: tone === "filled" ? "var(--j-tag-grey-bg)" : "var(--j-surface)",
        border: `1px solid ${tone === "filled" ? "transparent" : "var(--j-line-strong)"}`,
        fontSize: "var(--j-fs-sm)",
        fontWeight: 500,
        color: "var(--j-tag-grey-fg)",
        whiteSpace: "nowrap",
        "&[data-selected=true], &[aria-pressed=true]": {
            background: "var(--j-accent-soft)",
            borderColor: "transparent",
            color: "var(--j-accent-ink)"
        },
        "& > .chip-label": {
            overflow: "hidden",
            textOverflow: "ellipsis",
            minWidth: 0
        },
        "&[data-removable=true]": {
            paddingRight: "var(--j-sp-0-5)"
        }
    });
const notTone = {
    shouldForwardProp: (prop)=>prop !== "tone"
};
const ChipRoot = styled("span", notTone)(chipStyles);
const ChipButton = styled("button", notTone)((props)=>({
        ...chipStyles(props),
        ...pressable,
        cursor: "pointer"
    }));
const RemoveButton = styled("button")({
    ...focusRing,
    width: "var(--j-icon)",
    height: "var(--j-icon)",
    padding: 0,
    border: 0,
    borderRadius: "var(--j-r-xs)",
    background: "none",
    color: "var(--j-ink-3)",
    display: "grid",
    placeItems: "center",
    cursor: "pointer",
    "&:hover": {
        background: "var(--j-line)",
        color: "var(--j-ink)"
    }
});
export function Chip({ children, selected, tone = "outline", icon, onClick, onRemove, removeLabel, className }) {
    const { t } = useTranslation();
    const label = /*#__PURE__*/ React.createElement("span", {
        className: "chip-label"
    }, children);
    if (onClick && !onRemove) {
        return /*#__PURE__*/ React.createElement(ChipButton, {
            type: "button",
            tone: tone,
            className: className,
            "aria-pressed": selected,
            onClick: onClick
        }, icon, label);
    }
    return /*#__PURE__*/ React.createElement(ChipRoot, {
        tone: tone,
        className: className,
        "data-selected": selected,
        "data-removable": !!onRemove
    }, icon, label, onRemove && /*#__PURE__*/ React.createElement(RemoveButton, {
        type: "button",
        "aria-label": removeLabel ?? t("ui.remove"),
        onClick: onRemove
    }, /*#__PURE__*/ React.createElement(CloseIcon, {
        size: 12
    })));
}
const GREY_TAG = {
    background: "var(--j-tag-grey-bg)",
    color: "var(--j-tag-grey-fg)"
};
const status = (colour)=>({
        background: "none",
        color: "inherit",
        gap: "var(--j-sp-1-5)",
        padding: 0,
        "&::before": {
            content: '""',
            width: "0.4375rem",
            height: "0.4375rem",
            borderRadius: "50%",
            background: colour,
            flexShrink: 0
        }
    });
const TAG_STYLE = {
    primary: GREY_TAG,
    grey: GREY_TAG,
    ok: status("var(--j-ok)"),
    warn: status("var(--j-warn)")
};
export const Tag = styled("span", {
    shouldForwardProp: (prop)=>prop !== "tone"
})(({ tone = "primary" })=>({
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        height: "var(--j-tag-h)",
        padding: "0 var(--j-sp-1-5)",
        borderRadius: "var(--j-r-sm)",
        fontSize: "var(--j-fs-sm)",
        fontWeight: 500,
        lineHeight: 1,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        ...TAG_STYLE[tone]
    }));
const AvatarRoot = styled("span")({
    width: "var(--j-av)",
    height: "var(--j-av)",
    borderRadius: "50%",
    background: "var(--j-well)",
    color: "var(--j-ink-2)",
    display: "grid",
    placeItems: "center",
    flexShrink: 0,
    fontSize: "var(--j-fs-2xs)",
    lineHeight: 1,
    fontWeight: 600,
    letterSpacing: ".02em",
    textTransform: "uppercase",
    overflow: "hidden"
});
const LARGE_AVATAR = 32;
export function Avatar({ initials, size, className }) {
    const text = (initials ?? "").slice(0, 3);
    const style = size ? {
        width: size,
        height: size,
        fontSize: size >= LARGE_AVATAR ? "var(--j-fs-xs)" : undefined
    } : undefined;
    return /*#__PURE__*/ React.createElement(AvatarRoot, {
        className: className,
        style: style,
        "aria-hidden": true
    }, text);
}
